import React, { useState, useEffect } from 'react';
import './CashOutPage.css';
import TopBar from './TopBar';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

import { createCashout, listCashoutsByUser, getLastCashout, calculateCooldownFee } from '../services/cashoutService';
import { updateWalletBalance } from '../services/profileService';


export default function CashOutPage() {
  const { user, profile, refreshProfile } = useAuth();
  const { showSuccess, showError } = useToast();

  const [amountRequested, setAmountRequested] = useState('');
  const [payoutTier, setPayoutTier] = useState('1_week');
  const [bankInfo, setBankInfo] = useState({ bank_name: '', account_holder_name: '', account_number: '' });
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [lastCashout, setLastCashout] = useState(null);
  const [elapsedText, setElapsedText] = useState('');



  useEffect(() => {
    fetchHistory();
  }, [user]);

  useEffect(() => {
    if (!lastCashout) {
      setElapsedText('');
      return;
    }

    const updateElapsed = () => {
      const last = new Date(lastCashout.$createdAt);
      const now = new Date();
      const diffMs = now - last;

      const seconds = Math.floor(diffMs / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (days > 0) {
        setElapsedText(`${days}d ${hours % 24}h ${minutes % 60}m since last request`);
      } else if (hours > 0) {
        setElapsedText(`${hours}h ${minutes % 60}m ${seconds % 60}s since last request`);
      } else if (minutes > 0) {
        setElapsedText(`${minutes}m ${seconds % 60}s since last request`);
      } else {
        setElapsedText(`${seconds}s since last request`);
      }
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [lastCashout]);


  const fetchHistory = async () => {
    if (!user) return;
    try {
      const docs = await listCashoutsByUser(user.$id);
      setHistory(docs);

      // Also get the very last one for fee calculation if we don't have it
      const last = await getLastCashout(user.$id);
      setLastCashout(last);
    } catch (error) {
      console.error('Failed to fetch cashout history', error);
    }
  };


  const feePct = calculateCooldownFee(lastCashout ? lastCashout.$createdAt : null);
  const parsedAmount = Number(amountRequested || 0);
  const feeAmount = Math.round((parsedAmount * feePct) * 100) / 100;
  const finalPayoutPreview = Math.round((parsedAmount - feeAmount) * 100) / 100;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !profile) { showError('Please login'); return; }

    const numeric = Number(amountRequested);
    if (!Number.isFinite(numeric) || numeric <= 0) { showError('Please enter a valid amount'); return; }
    const balance = Number(profile.wallet_balance || 0);
    if (numeric > balance) { showError('Amount exceeds your available balance'); return; }


    setLoading(true);
    try {
      const result = await createCashout(user.$id, numeric, bankInfo);
      const { document: doc, holdFunds } = result;


      if (holdFunds) {
        await updateWalletBalance(profile.$id, balance - numeric);
        await refreshProfile();
        await fetchHistory();

        showSuccess('Cashout request submitted successfully!');

        setAmountRequested('');
        setBankInfo({ bank_name: '', account_holder_name: '', account_number: '' });
      } else {
        await fetchHistory();
        showError('Request was rejected automatically (net payout too small). No funds were deducted.');
      }
    } catch (error) {
      console.error('Cashout submit error', error);
      showError(error, 'Failed to submit cashout request');
    } finally {

      setLoading(false);
    }
  };

  return (
    <div className="cashout-page">
      <TopBar />
      <main className="cashout-main">
        <div className="cashout-header">
          <h1>Request Cash Out</h1>
          {elapsedText && (
            <div className="elapsed-badge" title="Time passed since your last cashout request">
              <span className="pulse-icon"></span>
              {elapsedText}
            </div>
          )}
        </div>


        <div className="cashout-content">
          <div className="cashout-form">
            <h2>Your balance</h2>
            <div className="current-balance">
              <h3>RM {Number(profile?.wallet_balance || 0).toFixed(2)}</h3>
            </div>

            <form onSubmit={handleSubmit}>
              <label htmlFor="amount">Amount (RM)</label>
              <input
                id="amount"
                name="amount"
                type="number"
                step="0.01"
                min="0"
                value={amountRequested}
                onChange={e => setAmountRequested(e.target.value)}
                placeholder="Enter amount to cash out"
              />




              <label htmlFor="bank_name">Bank name</label>
              <input
                id="bank_name"
                name="bank_name"
                value={bankInfo.bank_name}
                onChange={e => setBankInfo({ ...bankInfo, bank_name: e.target.value })}
                placeholder="Bank name"
                autoComplete="organization"
              />

              <label htmlFor="account_holder_name">Account holder name</label>
              <input
                id="account_holder_name"
                name="account_holder_name"
                value={bankInfo.account_holder_name}
                onChange={e => setBankInfo({ ...bankInfo, account_holder_name: e.target.value })}
                placeholder="Account holder name"
                autoComplete="name"
              />

              <label htmlFor="account_number">Account number</label>
              <input
                id="account_number"
                name="account_number"
                value={bankInfo.account_number}
                onChange={e => setBankInfo({ ...bankInfo, account_number: e.target.value })}
                placeholder="Account number"
                autoComplete="off"
              />

              <div className="preview">
                <div>
                  <span className="fee-label">
                    Admin fee
                    <div className="tooltip-container">
                      <span className="tooltip-icon">?</span>
                      <div className="tooltip-popup">
                        <p>Admin fee decreases based on the time since last payout request.</p>
                        <p>1st checkout: 1%</p>
                        <p>1 week: 3%</p>
                        <p>2 weeks: 2%</p>
                        <p>1 month: 1%</p>
                      </div>

                    </div>:
                  </span>
                  <strong>{(feePct * 100).toFixed(1)}%</strong>
                </div>

                <div>
                  <span>Fee amount:</span>
                  <strong>RM {feeAmount.toFixed(2)}</strong>
                </div>
                <div>
                  <span>Payout Amount:</span>
                  <strong>RM {finalPayoutPreview.toFixed(2)}</strong>
                </div>
              </div>

              <button type="submit" className="cashout-btn" disabled={loading}>
                {loading ? 'Submitting…' : 'Request cash out'}
              </button>
            </form>

            <p className="hint">
              Note: We currently process payouts manually. Submitting a request will put the requested amount on hold while pending. Payment will be done on every Sunday. Same day requests won't be processed.
            </p>
          </div>

          <div className="cashout-history">
            <h2>Request history</h2>
            {history.length === 0 && <div className="empty">No requests yet.</div>}
            {history.map(h => (
              <div key={h.$id} className="history-item">
                <div className="history-top">
                  <div className="history-amount">
                    Requested: RM {Number(h.amount_requested).toFixed(2)}
                  </div>
                  <div className={`history-status status-${h.status}`}>
                    {h.status}
                  </div>
                </div>
                <div className="history-meta">
                  Estimated payout: RM {Number(h.final_payout_amount || 0).toFixed(2)}
                </div>
                <div className="history-meta">
                  Fee: {(Number(h.admin_fee_percentage || 0) * 100).toFixed(1)}%
                </div>

                <div className="history-meta">
                  Scheduled payout: {h.scheduled_payout_date ? new Date(h.scheduled_payout_date).toLocaleDateString() : '—'}
                </div>
                <div className="history-meta">
                  Requested: {new Date(h.$createdAt).toLocaleString()}
                </div>
                {h.admin_note && (
                  <div className="history-note">Admin note: {h.admin_note}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}